import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";

import Shield from "../components/Shield";
import uploadLog from "../modules/uploadLog";
import { resolveConfig } from "./faqConfig";
import { getArticle, getSection } from "./zendeskApi";
import { setBackHandler } from "./faqBridge";
import { Container, Spinner, ErrorState, FaqHeader, EmailFooter, DebugViewport } from "./faqUi";
import "../css/notice.css";
import "../css/faq.css";

const FaqArticle = (props) => {
  const config = resolveConfig(props.location.search);
  const isRtl = config.locale && config.locale.split("-")[0] === "ar";
  const articleId = props.match.params.id;

  // Back returns to the list. The app always enters via the list (redirect ->
  // /faq), so there is a history entry to pop; fall back to the list route.
  const goBack = useCallback(() => {
    if (props.history.length > 1) props.history.goBack();
    else props.history.push("/faq" + props.location.search);
  }, [props.history, props.location.search]);

  // Device back / header back -> return to the list (not close the webview).
  useEffect(() => {
    setBackHandler(goBack);
  }, [goBack]);

  // Article bodies link to other Zendesk Help Center articles
  // (e.g. https://support.kinemaster.com/hc/ko/articles/6863652509465).
  // Intercept those clicks and route to the in-app detail view by id instead
  // of opening the external Zendesk page, carrying the current query string so
  // the target resolves the same host/locale. Non help-center links fall
  // through to their default behavior.
  const onBodyClick = useCallback(
    (e) => {
      const anchor = e.target.closest && e.target.closest("a");
      if (!anchor) return;
      const id = (anchor.getAttribute("href") || "").match(/\/articles\/(\d+)/);
      const host = anchor.hostname || "";
      if (id && /(\.zendesk\.com|kinemaster\.com)$/.test(host)) {
        e.preventDefault();
        props.history.push("/faq/articles/" + id[1] + props.location.search);
      }
    },
    [props.history, props.location.search]
  );

  // Entering the detail page should start at the top, not inherit the list's
  // scroll position.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleId]);

  const [state, setState] = useState({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    if (!config.api) {
      setState({ status: "error" });
      return;
    }
    setState({ status: "loading" });
    getArticle(config.api, articleId)
      .then((article) => {
        if (!alive) return;
        setState({
          status: "done",
          title: article.title || "",
          html: article.body || "",
          category: "Common",
        });
        // Resolve the section name for the email category (best-effort).
        if (article.section_id) {
          getSection(config.api, article.section_id)
            .then((sec) => {
              if (alive && sec && sec.name) {
                setState((s) => (s.status === "done" ? { ...s, category: sec.name } : s));
              }
            })
            .catch(() => {});
        }
      })
      .catch(() => alive && setState({ status: "error" }));
    return () => {
      alive = false;
    };
  }, [config.api, articleId, attempt]);

  useEffect(() => {
    if (!config.api) {
      uploadLog(window.location.href, "ERROR", "FAQ article: invalid server/host");
    }
  }, [config.api]);

  return (
    <Container>
      <Shield>
        <Helmet>
          <title>KineMaster FAQ</title>
        </Helmet>
        <FaqHeader locale={config.locale} onBack={goBack} variant="article" />

        <div className={"faq-wrap" + (isRtl ? " textR" : "")}>
          {state.status === "loading" ? <Spinner /> : null}
          {state.status === "error" ? (
            <ErrorState
              message="본문을 불러오지 못했습니다."
              onRetry={config.api ? () => setAttempt((a) => a + 1) : undefined}
            />
          ) : null}
          {state.status === "done" ? (
            <>
              <h2 className="faq-article-title">{state.title}</h2>
              <div
                className="article-body"
                onClick={onBodyClick}
                dangerouslySetInnerHTML={{ __html: state.html }}
              />
              <EmailFooter locale={config.locale} category={state.category} />
            </>
          ) : null}
        </div>
        <DebugViewport />
      </Shield>
    </Container>
  );
};

export default FaqArticle;
